import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faDna } from '@fortawesome/pro-light-svg-icons';
import { Button } from 'react-bootstrap';
import { useNavigate } from 'react-router';

import { RerequisitionType } from '@customTypes/requisition';
import { getCreateRerequisitionPath, getDraftRequisitionDetailsPath, getPatientDetailsPath } from '@router/paths';

import './PatientOrderReanalysisBanner.scss';

export interface PatientOrderMethylationReanalysisBannerProps {
  patientId: string;
  requisitionId: string;
  methylationProductIds: string[];
  activeMethylationDraftId?: string;
}

export default function PatientOrderMethylationReanalysisBanner({
  patientId,
  requisitionId,
  methylationProductIds,
  activeMethylationDraftId,
}: PatientOrderMethylationReanalysisBannerProps) {
  const navigate = useNavigate();

  const isResume = Boolean(activeMethylationDraftId);

  const onPrimaryAction = () => {
    if (activeMethylationDraftId && patientId) {
      navigate(getDraftRequisitionDetailsPath(activeMethylationDraftId), {
        state: { prevPath: getPatientDetailsPath(patientId!) },
      });
      return;
    }

    const search = new URLSearchParams();
    methylationProductIds.forEach(id => search.append('product_ids', id));

    const methylationPath = getCreateRerequisitionPath({
      patientId,
      requisitionId,
      rerequisitionType: RerequisitionType.METHYLATION_REFLEX,
    });

    navigate({
      pathname: methylationPath,
      search: search.size > 0 ? `?${search.toString()}` : '',
    });
  };

  return (
    <div className='patient-detail-order-banner' role='region' aria-label='Methylation Reflex Available'>
      <div className='patient-detail-order-banner__lead'>
        <div className='patient-detail-order-banner__icon' aria-hidden>
          <FontAwesomeIcon icon={faDna as IconDefinition} />
        </div>

        <div className='patient-detail-order-banner__text'>
          <p className='patient-detail-order-banner__title'>Methylation Reflex Available</p>

          <p className='patient-detail-order-banner__description'>
            A methylation reflex of the test(s) originally processed can be ordered using the existing blood sample at
            no additional cost.
          </p>
        </div>
      </div>

      <div className='patient-detail-order-banner__action'>
        <Button
          className='patient-detail-order-banner__cta'
          onClick={onPrimaryAction}
          size='sm'
          variant='primary'
          aria-label={isResume ? 'Resume methylation reflex order' : 'Get started with methylation reflex order'}
        >
          {isResume ? 'Resume Order' : 'Get Started'}
        </Button>
      </div>
    </div>
  );
}
